import React from "react";
import { Spinner } from "./Spinner";
import { injectStyles } from "./inject-styles";

/* ------------------------------------------------------------------ */
/*  Button                                                            */
/* ------------------------------------------------------------------ */

export type ButtonVariant = "primary" | "secondary" | "ghost" | "destructive";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  /**
   * When true, the Button renders its single child element instead of a
   * <button>, merging props/styles onto it. Useful for wrapping <a> or
   * router Link components.
   */
  asChild?: boolean;
}

/* -- Styles -------------------------------------------------------- */

const css = /* css */ `
  .sf-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: var(--sf-space-2);
    border: 1px solid transparent;
    border-radius: var(--sf-radius-md);
    font-family: var(--sf-font-sans);
    font-weight: var(--sf-weight-medium);
    line-height: 1;
    cursor: pointer;
    transition: background var(--sf-transition-fast),
                border-color var(--sf-transition-fast),
                color var(--sf-transition-fast),
                box-shadow var(--sf-transition-fast),
                opacity var(--sf-transition-fast);
    white-space: nowrap;
    user-select: none;
    position: relative;
  }

  .sf-btn:focus-visible {
    outline: var(--sf-ring-width) solid var(--sf-ring-color);
    outline-offset: var(--sf-ring-offset);
  }

  .sf-btn:disabled,
  .sf-btn[aria-disabled="true"] {
    opacity: 0.5;
    pointer-events: none;
  }

  /* -- Sizes -------------------------------------------------------- */
  .sf-btn--sm { font-size: var(--sf-text-sm); padding: 6px 12px; height: 30px; }
  .sf-btn--md { font-size: var(--sf-text-base); padding: 8px 16px; height: 36px; }
  .sf-btn--lg { font-size: var(--sf-text-md); padding: 10px 20px; height: 42px; }

  /* -- Variants ----------------------------------------------------- */
  .sf-btn--primary {
    background: var(--sf-primary);
    color: var(--sf-text-inverse);
    border-color: var(--sf-primary);
    box-shadow: var(--sf-shadow-xs);
  }
  .sf-btn--primary:hover {
    background: var(--sf-primary-hover);
    border-color: var(--sf-primary-hover);
  }
  .sf-btn--primary:active {
    background: var(--sf-primary-active);
    border-color: var(--sf-primary-active);
  }

  .sf-btn--secondary {
    background: var(--sf-bg-elevated);
    color: var(--sf-text);
    border-color: var(--sf-border);
  }
  .sf-btn--secondary:hover {
    background: var(--sf-gray-50);
    border-color: var(--sf-border-strong);
  }
  .sf-btn--secondary:active {
    background: var(--sf-gray-100);
  }

  .sf-btn--ghost {
    background: transparent;
    color: var(--sf-text-secondary);
    border-color: transparent;
  }
  .sf-btn--ghost:hover {
    background: var(--sf-gray-100);
    color: var(--sf-text);
  }
  .sf-btn--ghost:active {
    background: var(--sf-gray-200);
  }

  .sf-btn--destructive {
    background: var(--sf-error);
    color: var(--sf-text-inverse);
    border-color: var(--sf-error);
  }
  .sf-btn--destructive:hover {
    background: #b52424;
    border-color: #b52424;
  }
  .sf-btn--destructive:active {
    background: #a12020;
    border-color: #a12020;
  }

  /* -- Loading state ------------------------------------------------ */
  .sf-btn--loading .sf-btn__label {
    visibility: hidden;
  }
  .sf-btn__spinner-overlay {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

/* -- Component ----------------------------------------------------- */

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "secondary",
      size = "md",
      loading = false,
      asChild = false,
      children,
      className,
      disabled,
      ...rest
    },
    ref,
  ) => {
    injectStyles("sf-btn", css);

    const classes = [
      "sf-btn",
      `sf-btn--${variant}`,
      `sf-btn--${size}`,
      loading ? "sf-btn--loading" : "",
      className ?? "",
    ]
      .filter(Boolean)
      .join(" ");

    const inner = (
      <>
        <span className="sf-btn__label">{children}</span>
        {loading && (
          <span className="sf-btn__spinner-overlay" aria-hidden="true">
            <Spinner
              size={size === "sm" ? 14 : size === "lg" ? 20 : 16}
              color="currentColor"
            />
          </span>
        )}
      </>
    );

    if (asChild && React.isValidElement(children)) {
      return React.cloneElement(children as React.ReactElement<any>, {
        ref,
        className: classes,
        "aria-disabled": disabled || loading || undefined,
        ...rest,
      });
    }

    return (
      <button
        ref={ref}
        className={classes}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...rest}
      >
        {inner}
      </button>
    );
  },
);

Button.displayName = "Button";
export { Button };
